import { FC, FormEvent, ReactNode } from 'react'
import classNames from 'classnames'
import Button from '@UI/New/Button'
import { InputWhite } from '@UI/New/InputWhite'
import SelectField from '@pages/Admin/CourseSettings/components/SelectField/SelectField'
import Preloader from '@UI/Preloader'
import sharedClasses from '@modals/index.module.css'
import type { ContractConstructorGroup } from '@pages/Admin/PlatformSettings2/SettingUpContracts/api/contractTemplateConstructor.types'
import {
  PARENT_GROUP_CODES,
  PRODUCT_GROUP_CODES,
} from '../utils/contractTemplateConstructor.utils'
import ConstructorGroupFields from './ConstructorGroupFields'
import classes from '../AddTemplateContractModal.module.css'

type SelectOption = {
  label: string
  value: string | null
}

type TemplateContractFormProps = {
  templateId: number | null
  templateOptions: Array<{ label: string; value: string }>
  templatesLoading: boolean
  constructorLoading: boolean
  parentLoading: boolean
  productLoading: boolean
  isSubmitting: boolean
  documentName: string
  values: Record<string, string>
  invalidFieldCodes: Set<string>
  recalculatingCodes: Set<string>
  constructorGroups: ContractConstructorGroup[]
  parentOptions: SelectOption[]
  productOptions: Array<{ label: string; value: string }>
  shouldShowProductSelector: boolean
  selectedParentId: number | null
  selectedProductKey: string
  hasConstructorData: boolean
  onTemplateChange: (value: string | string[] | null) => void
  onParentChange: (parentId: number | null) => void
  onProductChange: (productKey: string) => void
  onDocumentNameChange: (value: string) => void
  onFieldChange: (code: string, value: string) => void
  onSubmit: (event: FormEvent<HTMLFormElement>) => void
}

const TemplateContractForm: FC<TemplateContractFormProps> = ({
  templateId,
  templateOptions,
  templatesLoading,
  constructorLoading,
  parentLoading,
  productLoading,
  isSubmitting,
  documentName,
  values,
  invalidFieldCodes,
  recalculatingCodes,
  constructorGroups,
  parentOptions,
  productOptions,
  shouldShowProductSelector,
  selectedParentId,
  selectedProductKey,
  hasConstructorData,
  onTemplateChange,
  onParentChange,
  onProductChange,
  onDocumentNameChange,
  onFieldChange,
  onSubmit,
}) => {
  const parentSelector =
    parentOptions.length > 0 ? (
      <SelectField
        label="Выберите родителя"
        value={selectedParentId == null ? 'null' : String(selectedParentId)}
        options={parentOptions.map((option) => ({
          label: option.label,
          value: option.value == null ? 'null' : String(option.value),
        }))}
        placeholder="Выберите родителя"
        className={classes.selectField}
        onChange={(value) => {
          if (value == null || value === 'null') {
            onParentChange(null)
            return
          }
          onParentChange(Number(value))
        }}
      />
    ) : null

  const productSelector = shouldShowProductSelector ? (
    <SelectField
      label="Выберите курс/класс"
      value={selectedProductKey}
      options={productOptions}
      placeholder="Выберите курс/класс"
      className={classes.selectField}
      onChange={(value) => {
        onProductChange(String(value ?? 'custom'))
      }}
    />
  ) : null

  const getGroupSelector = (groupCode: string): ReactNode => {
    if (PARENT_GROUP_CODES.has(groupCode)) return parentSelector
    if (PRODUCT_GROUP_CODES.has(groupCode)) return productSelector
    return null
  }

  const isSubmitDisabled =
    templateId == null ||
    !hasConstructorData ||
    constructorLoading ||
    parentLoading ||
    productLoading ||
    isSubmitting ||
    !documentName.trim()

  return (
    <form
      onSubmit={onSubmit}
      className={classNames(sharedClasses.contentWrapper, classes.form)}
    >
      <div className={classes.content}>
        <SelectField
          label="Выберите шаблон"
          value={templateId != null ? String(templateId) : null}
          options={templateOptions}
          placeholder="Выберите шаблон"
          loading={templatesLoading}
          className={classes.selectField}
          onChange={onTemplateChange}
        />

        {constructorLoading ? (
          <div className={classes.loader}>
            <Preloader />
          </div>
        ) : null}

        {hasConstructorData && !constructorLoading ? (
          <>
            <InputWhite
              title="Название договора"
              value={documentName}
              onChange={onDocumentNameChange}
              placeholder="Введите название договора"
              className={classes.input}
            />

            {constructorGroups.map((group) => (
              <ConstructorGroupFields
                key={group.code}
                group={group}
                values={values}
                invalidFieldCodes={invalidFieldCodes}
                recalculatingCodes={recalculatingCodes}
                onChange={onFieldChange}
                groupSelector={getGroupSelector(group.code)}
                isLoading={
                  (parentLoading && PARENT_GROUP_CODES.has(group.code)) ||
                  (productLoading && PRODUCT_GROUP_CODES.has(group.code))
                }
              />
            ))}
          </>
        ) : null}
      </div>

      <div className={classes.footer}>
        <Button
          type="submit"
          color="primary"
          disabled={isSubmitDisabled}
          isLoading={isSubmitting}
          className={classes.submitButton}
        >
          Сохранить
        </Button>
      </div>
    </form>
  )
}

export default TemplateContractForm
